/**
 * @file index.tsx
 *
 * @fileoverview Renders social media links.
 */
import React from 'react';
import { useIntl } from 'react-intl';
import { FlexItems } from '~/vcl/atoms/06-layout/03-flex';
import { FlexItem } from '~/vcl/atoms/06-layout/03-flex/02-flex-item';
import FacebookColorIcon from '~/vcl/icons/color/Facebook';
import LinkedinColorIcon from '~/vcl/icons/color/Linkedin';
import TwitterColorIcon from '~/vcl/icons/color/Twitter';
import YoutubeColorIcon from '~/vcl/icons/color/Youtube';
import InstagramColorIcon from '~/vcl/icons/color/Instagram';
import XingColorIcon from '~/vcl/icons/color/Xing';
import TiktokColorIcon from '~/vcl/icons/color/Tiktok';
import WhatsappColorIcon from '~/vcl/icons/color/Whatsapp';
import TelegramColorIcon from '~/vcl/icons/color/Telegram';
import PinterestColorIcon from '~/vcl/icons/color/Pinterest';
import styles from './socialMediaLinks.module.scss';
import FacebookIcon from '~/vcl/icons/Facebook';
import LinkedinIcon from '~/vcl/icons/Linkedin';
import TwitterIcon from '~/vcl/icons/Twitter';
import YoutubeIcon from '~/vcl/icons/Youtube';
import InstagramIcon from '~/vcl/icons/Instagram';
import XingIcon from '~/vcl/icons/Xing';
import TiktokIcon from '~/vcl/icons/Tiktok';
import WhatsappIcon from '~/vcl/icons/Whatsapp';
import TelegramIcon from '~/vcl/icons/Telegram';
import PinterestIcon from '~/vcl/icons/Pinterest';
import ThreadsColorIcon from '~/vcl/icons/color/Threads';
import ThreadsIcon from '~/vcl/icons/Threads';
import BlueskyColorIcon from '~/vcl/icons/color/Bluesky';
import BlueskyIcon from '~/vcl/icons/Bluesky';

interface SocialMediaLinksProps {
  hrefs: {
    facebook?: string | null;
    twitter?: string | null;
    linkedin?: string | null;
    youtube?: string | null;
    xing?: string | null;
    instagram?: string | null;
    tiktok?: string | null;
    whatsapp?: string | null;
    telegram?: string | null;
    pinterest?: string | null;
    threads?: string | null;
    bluesky?: string | null;
  };
  color?: string;
  /**
   * Boolean whether to use colored icons.
   */
  coloredIcons?: boolean;
}

export function SocialMediaLinkItem({ href, title, IconComponent, color }) {
  return (
    <FlexItem margin={4}>
      <a
        href={href}
        target="_blank"
        rel="noopener noreferrer"
        className={styles.socialMediaLinksButton}
      >
        <IconComponent color={color} height="25" width="25" title={title} />
      </a>
    </FlexItem>
  );
}

export default function SocialMediaLinks({
  hrefs,
  color,
  coloredIcons = true,
}: SocialMediaLinksProps) {
  const intl = useIntl();

  return (
    <FlexItems>
      {hrefs.facebook && (
        <SocialMediaLinkItem
          href={hrefs.facebook}
          title={intl.formatMessage({
            id: 'facebook_logo',
            defaultMessage: 'Facebook logo',
          })}
          IconComponent={coloredIcons ? FacebookColorIcon : FacebookIcon}
          // If single color icons are used and color is defined pass it to the icon.
          color={!coloredIcons && color ? color : undefined}
        />
      )}
      {hrefs.linkedin && (
        <SocialMediaLinkItem
          href={hrefs.linkedin}
          title={intl.formatMessage({
            id: 'linkedin_logo',
            defaultMessage: 'LinkedIn logo',
          })}
          IconComponent={coloredIcons ? LinkedinColorIcon : LinkedinIcon}
          // If single color icons are used and color is defined pass it to the icon.
          color={!coloredIcons && color ? color : undefined}
        />
      )}
      {hrefs.twitter && (
        <SocialMediaLinkItem
          href={hrefs.twitter}
          title={intl.formatMessage({
            id: 'twitter_logo',
            defaultMessage: 'Twitter logo',
          })}
          IconComponent={coloredIcons ? TwitterColorIcon : TwitterIcon}
          // If single color icons are used and color is defined pass it to the icon.
          color={!coloredIcons && color ? color : undefined}
        />
      )}
      {hrefs.youtube && (
        <SocialMediaLinkItem
          href={hrefs.youtube}
          title={intl.formatMessage({
            id: 'youtube_logo',
            defaultMessage: 'Youtube logo',
          })}
          IconComponent={coloredIcons ? YoutubeColorIcon : YoutubeIcon}
          // If single color icons are used and color is defined pass it to the icon.
          color={!coloredIcons && color ? color : undefined}
        />
      )}
      {hrefs.instagram && (
        <SocialMediaLinkItem
          href={hrefs.instagram}
          title={intl.formatMessage({
            id: 'instagram_logo',
            defaultMessage: 'Instagram logo',
          })}
          IconComponent={coloredIcons ? InstagramColorIcon : InstagramIcon}
          // If single color icons are used and color is defined pass it to the icon.
          color={!coloredIcons && color ? color : undefined}
        />
      )}
      {hrefs.xing && (
        <SocialMediaLinkItem
          href={hrefs.xing}
          title={intl.formatMessage({
            id: 'xing_logo',
            defaultMessage: 'Xing logo',
          })}
          IconComponent={coloredIcons ? XingColorIcon : XingIcon}
          // If single color icons are used and color is defined pass it to the icon.
          color={!coloredIcons && color ? color : undefined}
        />
      )}
      {hrefs.tiktok && (
        <SocialMediaLinkItem
          href={hrefs.tiktok}
          title={intl.formatMessage({
            id: 'tiktok_logo',
            defaultMessage: 'TikTok logo',
          })}
          IconComponent={coloredIcons ? TiktokColorIcon : TiktokIcon}
          // If single color icons are used and color is defined pass it to the icon.
          color={!coloredIcons && color ? color : undefined}
        />
      )}
      {hrefs.whatsapp && (
        <SocialMediaLinkItem
          href={hrefs.whatsapp}
          title={intl.formatMessage({
            id: 'whatsapp_logo',
            defaultMessage: 'Whatsapp logo',
          })}
          IconComponent={coloredIcons ? WhatsappColorIcon : WhatsappIcon}
          // If single color icons are used and color is defined pass it to the icon.
          color={!coloredIcons && color ? color : undefined}
        />
      )}
      {hrefs.telegram && (
        <SocialMediaLinkItem
          href={hrefs.telegram}
          title={intl.formatMessage({
            id: 'telegram_logo',
            defaultMessage: 'Telegram logo',
          })}
          IconComponent={coloredIcons ? TelegramColorIcon : TelegramIcon}
          // If single color icons are used and color is defined pass it to the icon.
          color={!coloredIcons && color ? color : undefined}
        />
      )}
      {hrefs.pinterest && (
        <SocialMediaLinkItem
          href={hrefs.pinterest}
          title={intl.formatMessage({
            id: 'pinterest_logo',
            defaultMessage: 'Pinterest logo',
          })}
          IconComponent={coloredIcons ? PinterestColorIcon : PinterestIcon}
          // If single color icons are used and color is defined pass it to the icon.
          color={!coloredIcons && color ? color : undefined}
        />
      )}
      {hrefs.threads && (
        <SocialMediaLinkItem
          href={hrefs.threads}
          title={intl.formatMessage({
            id: 'threads_logo',
            defaultMessage: 'Threads logo',
          })}
          IconComponent={coloredIcons ? ThreadsColorIcon : ThreadsIcon}
          // If single color icons are used and color is defined pass it to the icon.
          color={!coloredIcons && color ? color : undefined}
        />
      )}
      {hrefs.bluesky && (
        <SocialMediaLinkItem
          href={hrefs.bluesky}
          title={intl.formatMessage({
            id: 'bluesky_logo',
            defaultMessage: 'Bluesky logo',
          })}
          IconComponent={coloredIcons ? BlueskyColorIcon : BlueskyIcon}
          // If single color icons are used and color is defined pass it to the icon.
          color={!coloredIcons && color ? color : undefined}
        />
      )}
    </FlexItems>
  );
}
export { SocialMediaLinks };
